// Logica de la liquidacion con montos en el portal (LQ-4, ADR-026 §6 y
// ADR-025; LQ-6, §5): paths, textos en castellano, estado de la revision y
// montos formateados desde enteros de la unidad menor, nunca double. Sin React
// ni reloj. Unico modulo del portal que nombra dinero (regla 7): el exportable
// de LQ-1 sigue sin montos.
import {
  SETTLEMENT_DISPUTE_COMMENT_MAX,
  type ChargeConcept,
  type CreateSettlementDisputeRequest,
  type ProviderSettlementSummary,
  type SettlementAdjustmentReason,
  type SettlementDisputeReason,
  type SettlementDisputeResolution,
  type SettlementDraft,
  type SettlementLine,
  type SettlementParty,
  type SettlementPeriodStatus,
  type SettlementReview,
  type TripSettlement,
  type TripWait,
  type TripWaitStop,
  type UnchargedReason,
  type WaitGap,
  type WaitSource,
} from "@ucorp/contracts";
import { isCalendarDate, type TripExportFormat } from "./trip-export.js";

/** Tope de las listas (rediseño, decision 1): plantas en el resumen y viajes en el detalle. */
export const SETTLEMENT_SITES_SHOWN = 5;
export const SETTLEMENT_TRIPS_SHOWN = 10;

export function settlementSummaryPath(date?: string): string {
  return date === undefined ? "/provider/settlements" : `/provider/settlements?date=${encodeURIComponent(date)}`;
}

export function settlementDraftPath(siteId: string, date: string, format: TripExportFormat): string {
  const suffix = format === "json" ? "" : `.${format}`;
  return `/sites/${encodeURIComponent(siteId)}/provider/settlements/draft${suffix}?date=${encodeURIComponent(date)}`;
}

export { isCalendarDate };

const MINOR_PER_UNIT = 100;

function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/** Entero de la unidad menor a "$ 1.250,00" (ARS) o "USD 10,00", por aritmetica entera. */
export function formatMinor(amountMinor: number, currency: string): string {
  const sign = amountMinor < 0 ? "-" : "";
  const abs = Math.abs(Math.trunc(amountMinor));
  const units = Math.floor(abs / MINOR_PER_UNIT);
  const cents = abs - units * MINOR_PER_UNIT;
  const prefix = currency === "ARS" ? "$" : currency;
  return `${sign}${prefix} ${groupThousands(String(units))},${String(cents).padStart(2, "0")}`;
}

/** Puntos basicos a "20,00 %". */
export function formatBasisPoints(basisPoints: number): string {
  const abs = Math.abs(Math.trunc(basisPoints));
  const units = Math.floor(abs / 100);
  return `${basisPoints < 0 ? "-" : ""}${groupThousands(String(units))},${String(abs - units * 100).padStart(2, "0")} %`;
}

/** Metros enteros a km con tres decimales y coma. */
export function formatKm(meters: number): string {
  const digits = String(Math.abs(meters)).padStart(4, "0");
  return `${meters < 0 ? "-" : ""}${digits.slice(0, -3)},${digits.slice(-3)}`;
}

export const CONCEPT_LABEL: Record<ChargeConcept, string> = {
  per_trip: "Salida",
  per_km: "Por kilómetro",
  per_hour: "Disposición por hora",
  wait: "Espera",
  per_passenger: "Por pasajero",
  minimum: "Mínimo del viaje",
  night_surcharge: "Recargo nocturno",
  holiday_surcharge: "Recargo por feriado",
  exception_surcharge: "Recargo por excepción",
  cancellation: "Cancelación tardía",
};

export const UNCHARGED_LABEL: Record<UnchargedReason, string> = {
  no_rate_card: "sin tarifario para el tipo de servicio",
  no_version_in_force: "sin tarifa vigente en la fecha",
};

export const EXPORT_ONLY_COPY = "Esta planta liquida con el exportable de viajes, sin montos en UCORP: cada lado le pone precio en su sistema.";

/** Frase de una linea desde sus campos ("37,400 km planificados x $ 1.250,00 = $ 46.750,00", §4.2). */
export function lineText(line: SettlementLine): string {
  const amount = formatMinor(line.amountMinor, line.currency);
  const unit = formatMinor(line.unitAmountMinor, line.currency);
  switch (line.unit) {
    case "trip":
      return line.concept === "minimum" ? `completa el mínimo de ${unit}: ${amount}` : unit;
    case "meter":
      return `${formatKm(line.quantity)} km ${line.basis?.field === "actual_meters" ? "reales" : "planificados"} x ${unit} = ${amount}`;
    case "block":
      return `${String(line.quantity)} ${line.quantity === 1 ? "bloque" : "bloques"} x ${unit} = ${amount}`;
    case "passenger":
      return `${String(line.quantity)} ${line.quantity === 1 ? "pasajero" : "pasajeros"} x ${unit} = ${amount}`;
    case "basis_point":
      return `${formatBasisPoints(line.quantity)} sobre ${unit} = ${amount}`;
  }
}

/** Estado del viaje en una frase corta: su total, o por que no se cobro. */
export function tripTotalText(trip: Pick<TripSettlement, "uncharged" | "totalMinor">, currency: string): string {
  return trip.uncharged === undefined ? formatMinor(trip.totalMinor, currency) : `Sin cobrar: ${UNCHARGED_LABEL[trip.uncharged]}`;
}

/** "3 cobrados de 4 sellados; 1 sin tarifa; 2 sin sellar todavía". */
export function tripsCountText(draft: Pick<SettlementDraft, "totals" | "unsealedTrips">): string {
  const parts = [`${String(draft.totals.chargedTripCount)} cobrados de ${String(draft.totals.tripCount)} sellados`];
  if (draft.totals.unchargedTripCount > 0) parts.push(`${String(draft.totals.unchargedTripCount)} sin tarifa`);
  if (draft.unsealedTrips > 0) parts.push(`${String(draft.unsealedTrips)} sin sellar todavía`);
  return parts.join("; ");
}

/** Texto de la columna de totales del resumen multiplanta. */
export function summaryTotalText(item: ProviderSettlementSummary): string {
  return item.mode === "full" ? formatMinor(item.totalMinor, item.currency) : "Exportable sin montos";
}

/** Plantas del resumen con el filtro aplicado: con "Todas", todas; con una planta, solo esa. */
export function summaryForSite(items: readonly ProviderSettlementSummary[], siteId: string | null): ProviderSettlementSummary[] {
  return siteId === null ? [...items] : items.filter((item) => item.siteId === siteId);
}

/** Los primeros N y cuantos quedan. */
export function limited<T>(rows: readonly T[], n: number, all: boolean): { shown: T[]; rest: number } {
  return all ? { shown: [...rows], rest: 0 } : { shown: rows.slice(0, n), rest: Math.max(0, rows.length - n) };
}

// --- Revision del periodo (LQ-6, ADR-026 §5) -----------------------------------------
// El proveedor ve el estado y el plazo, da conformidad al borrador vigente y
// observa viajes o lineas; UCORP opera las transiciones desde la consola. Sin
// identidades: la API no las manda.

export const PERIOD_STATUS_LABEL: Record<SettlementPeriodStatus, string> = {
  open: "Abierto",
  in_review: "En revisión",
  closed: "Cerrado",
};

export const PARTY_LABEL: Record<SettlementParty, string> = { site: "La planta", provider: "La empresa" };

export const DISPUTE_REASON_LABEL: Record<SettlementDisputeReason, string> = {
  distance: "Kilómetros",
  wait: "Espera",
  passengers: "Pasajeros",
  outcome: "Resultado del viaje",
  cancellation: "Cancelación",
  rate: "Tarifa aplicada",
  cost_center: "Reparto por centro de costos",
  other: "Otro",
};

export const DISPUTE_REASONS = Object.keys(DISPUTE_REASON_LABEL) as SettlementDisputeReason[];

export const DISPUTE_RESOLUTION_LABEL: Record<SettlementDisputeResolution, string> = {
  facts_corrected: "UCORP corrigió los hechos",
  rate_corrected: "UCORP corrigió la tarifa",
  rejected: "UCORP la rechazó",
  deferred_to_adjustment: "Se cierra igual: la diferencia sale como nota de ajuste",
};

export const ADJUSTMENT_REASON_LABEL: Record<SettlementAdjustmentReason, string> = {
  fact_correction: "corrección de hechos posterior al cierre",
  rate_change: "tarifa con vigencia retroactiva",
  dispute_resolution: "observación diferida a ajuste",
};

export const DISPUTE_COMMENT_MAX = SETTLEMENT_DISPUTE_COMMENT_MAX;

export function approvePath(siteId: string, periodId: string): string {
  return `/sites/${encodeURIComponent(siteId)}/provider/settlements/periods/${encodeURIComponent(periodId)}/approve`;
}

export function disputePath(siteId: string, periodId: string): string {
  return `/sites/${encodeURIComponent(siteId)}/provider/settlements/periods/${encodeURIComponent(periodId)}/disputes`;
}

/** Conformidad de una parte: nunca la dio, la dio sobre el borrador vigente, o sobre uno anterior. */
export type ApprovalState = "none" | "current" | "stale";

export function approvalState(review: Pick<SettlementReview, "approvals">, party: SettlementParty): ApprovalState {
  const own = review.approvals.filter((a) => a.party === party);
  if (own.length === 0) return "none";
  return own.some((a) => a.current) ? "current" : "stale";
}

export const APPROVAL_TEXT: Record<ApprovalState, string> = {
  none: "sin conformidad",
  current: "conforme",
  stale: "conforme con una versión anterior",
};

export function openDisputeCount(review: Pick<SettlementReview, "disputes">): number {
  return review.disputes.filter((d) => d.status === "open").length;
}

/**
 * La misma regla que la API para el comentario de una observacion: sin mails,
 * enlaces ni numeros largos (telefonos, documentos, legajos). Fechas y horas
 * pasan. Evita mandar un pedido que ya se sabe rechazado; la API decide igual.
 */
export type DisputeCommentProblem = "email" | "link" | "long_number" | "too_long";

export function disputeCommentProblem(comment: string): DisputeCommentProblem | null {
  if (comment.length > DISPUTE_COMMENT_MAX) return "too_long";
  if (/@/.test(comment)) return "email";
  if (/(?:https?:\/\/|www\.)/i.test(comment)) return "link";
  const withoutDates = comment.replace(/\b\d{4}-\d{2}-\d{2}\b/g, " ").replace(/\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/g, " ").replace(/\b\d{1,2}:\d{2}\b/g, " ");
  if (/\d(?:[\s.-]?\d){5,}/.test(withoutDates)) return "long_number";
  return null;
}

export const DISPUTE_COMMENT_PROBLEM_TEXT: Record<DisputeCommentProblem, string> = {
  email: "Sin direcciones de mail.",
  link: "Sin enlaces.",
  long_number: "Sin números largos: ni teléfonos, ni documentos, ni legajos.",
  too_long: `Hasta ${String(DISPUTE_COMMENT_MAX)} caracteres.`,
};

/** Body de la observacion a partir del formulario; null si el comentario no pasa. */
export function disputeRequest(input: { tripId: string; rateLineId?: string | undefined; reason: SettlementDisputeReason; comment: string }): CreateSettlementDisputeRequest | null {
  const comment = input.comment.trim();
  if (comment !== "" && disputeCommentProblem(comment) !== null) return null;
  return {
    tripId: input.tripId,
    ...(input.rateLineId === undefined || input.rateLineId === "" ? {} : { rateLineId: input.rateLineId }),
    reason: input.reason,
    ...(comment === "" ? {} : { comment }),
  };
}

// --- Espera en la liquidacion (LQ-8, ADR-026 §3.5, ADR-029 §5) -------------------
// La espera del viaje con su fuente, lo que cobra la linea de espera despues
// de la tolerancia de cobro del tarifario (que no es la de no-show de la
// planta) y el detalle por parada para auditarla. Horas en la zona de la
// planta que manda la API (regla 9). Sin personas: horas y cantidades.

export const WAIT_SOURCE_LABEL: Record<WaitSource, string> = {
  driver_app: "Registrada por el conductor",
  estimated: "Estimada por GPS",
};

export const WAIT_GAP_LABEL: Record<WaitGap, string> = {
  no_driver_arrival: "el conductor no marcó la llegada a una parada desde la app",
  passenger_unresolved: "quedó un pasajero sin marcar como subió o no se presentó",
  no_roster: "el viaje no tiene nómina con parada de subida",
};

/** Espera de cada viaje del borrador, por id. */
export function waitsByTrip(draft: Pick<SettlementDraft, "waits">): Map<string, TripWait> {
  return new Map((draft.waits ?? []).map((wait) => [wait.tripId, wait]));
}

/** "11 min" o "Sin dato" para la columna Espera. */
export function waitMinutesText(wait: TripWait | undefined): string {
  return wait?.waitMinutes === undefined ? "Sin dato" : `${String(wait.waitMinutes)} min`;
}

/** "6 min facturables" debajo de los minutos; null si el tarifario no cobra espera. */
export function waitBillableText(wait: TripWait | undefined): string | null {
  if (wait?.billing === undefined) return null;
  return `${String(wait.billing.billableMinutes)} min facturables`;
}

/** La frase que explica el cobro: minutos, tolerancia de cobro del tarifario (una vez por viaje, decision de Federico del 29/10) y bloques. */
export function waitBillingText(wait: TripWait): string {
  const minutes = waitMinutesText(wait);
  if (wait.billing === undefined) return `${minutes}. El tarifario de este viaje no cobra espera.`;
  const { toleranceMinutes, billableMinutes, blocks, blockMinutes } = wait.billing;
  const blocksText = `${String(blocks)} ${blocks === 1 ? "bloque" : "bloques"} de ${String(blockMinutes)} min`;
  return `${minutes} de espera, menos ${String(toleranceMinutes)} min de tolerancia de cobro por viaje del tarifario: ${String(billableMinutes)} min facturables, ${blocksText}.`;
}

/** Por que el viaje quedo con la estimada por GPS; null si es registrada. */
export function waitFallbackText(wait: TripWait): string | null {
  if (wait.waitSource === "driver_app") return null;
  return wait.gap === undefined ? "Sellado sin espera registrada completa." : `Sin espera registrada: ${WAIT_GAP_LABEL[wait.gap]}.`;
}

/** HH:MM:SS de un instante en la zona de la planta; la espera se audita al segundo. */
export function clockSeconds(instant: string | undefined, timezone: string): string {
  if (instant === undefined) return "";
  const time = Date.parse(instant);
  if (Number.isNaN(time)) return "";
  try {
    return new Intl.DateTimeFormat("es-AR", { hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23", timeZone: timezone }).format(time);
  } catch {
    return "";
  }
}

/** "7 min 30 s", "45 s", "0 s". */
export function durationText(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds - minutes * 60;
  if (minutes === 0) return `${String(rest)} s`;
  return rest === 0 ? `${String(minutes)} min` : `${String(minutes)} min ${String(rest)} s`;
}

export interface WaitStopRow {
  readonly key: string;
  /** "Parada 1": el orden mas uno, sin direccion. */
  readonly stop: string;
  readonly planned: string;
  readonly arrived: string;
  readonly from: string;
  /** Hasta cuando cuenta y por que termina ahi. */
  readonly until: string;
  readonly waited: string;
  /** "Subió 06:06:00 · No se presentó 06:19:00". */
  readonly events: string;
}

/** Filas del detalle por parada, en la zona de la planta. */
export function waitStopRows(wait: TripWait, timezone: string): WaitStopRow[] {
  return wait.stops.map((stop: TripWaitStop) => {
    const events = [
      ...stop.boardedAt.map((at) => `Subió ${clockSeconds(at, timezone)}`),
      ...stop.noShowAt.map((at) => `No se presentó ${clockSeconds(at, timezone)}`),
      ...(stop.unresolved > 0 ? [`${String(stop.unresolved)} sin marcar`] : []),
    ];
    const until = stop.endedAt === undefined
      ? stop.gap === undefined ? "" : WAIT_STOP_GAP_LABEL[stop.gap]
      : `${clockSeconds(stop.endedAt, timezone)}${stop.cappedByNextArrival ? " (llegó a la parada siguiente)" : ""}`;
    return {
      key: String(stop.order),
      stop: `Parada ${String(stop.order + 1)}`,
      planned: clockSeconds(stop.plannedAt, timezone),
      arrived: stop.driverArrivedAt === undefined ? "Sin llegada de la app" : clockSeconds(stop.driverArrivedAt, timezone),
      from: clockSeconds(stop.waitStartedAt, timezone),
      until,
      waited: stop.waitSeconds === undefined ? "Sin dato" : durationText(stop.waitSeconds),
      events: events.join(" · "),
    };
  });
}

const WAIT_STOP_GAP_LABEL: Record<WaitGap, string> = {
  no_driver_arrival: "Sin llegada del conductor",
  passenger_unresolved: "Falta marcar a un pasajero",
  no_roster: "Sin nómina",
};
